// API client del módulo Marketing (Marketing OS · F1).
// Contrato: /root/docs/67-marketing-os-agentico.md §6. Backend (mcp_django):
//   GET  /api/v1/admin/marketing/resumen?desde&hasta   (marketing:read)
//   GET  /api/v1/admin/marketing/presupuesto?mes        (marketing:budget)
//   PUT  /api/v1/admin/marketing/presupuesto?mes        (marketing:budget)
//   GET  /api/v1/admin/marketing/medicion               (marketing:read)
//   GET  /api/v1/admin/marketing/campanas?canal&estado  (marketing:read)
// La org sale SIEMPRE del JWT (nunca la mandamos). Montos en centavos.
//
// Datos de demostración: solo con VITE_MARKETING_OS_MOCK=1 o si el usuario los
// pide en pantalla mientras el backend aún no responde (sessionStorage). En
// cuanto el backend contesta, la pantalla usa SOLO datos reales.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import { mockMedicion, mockPresupuesto, mockResumen } from './mock';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/marketing';

// ── Tipos (idénticos al contrato) ─────────────────────────────

export type Canal = 'meta' | 'google' | 'organico' | 'whatsapp' | 'correo' | 'prensa' | 'directo' | string;
export type Ritmo = 'ok' | 'alto' | 'bajo';
export type Severidad = 'info' | 'warn' | 'critical';

export interface PresupuestoCanal {
  canal: Canal;
  tope_cents: number;
  gastado_cents?: number;
}

export interface Totales {
  gasto_cents: number;
  impresiones: number;
  clics: number;
  leads: number;
  compras: number;
  ingreso_cents: number;
  cpl_cents: number | null;
  cpa_cents: number | null;
  roas: number | null;
}

export interface FilaCanal {
  canal: Canal;
  gasto_cents: number;
  leads: number;
  compras: number;
  ingreso_cents: number;
  cpl_cents: number | null;
  cpa_cents: number | null;
}

export interface FilaCampana {
  registry_id: number;
  canal: Canal;
  nombre: string;
  estado: string;
  cuenta: string;
  gasto_cents: number;
  impresiones: number;
  clics: number;
  leads: number;
  compras: number;
  cpl_cents: number | null;
}

export interface PuntoDiario {
  fecha: string;
  gasto_cents: number;
  leads: number;
  compras: number;
}

export interface Alerta {
  tipo: 'ritmo' | 'cpl' | 'tracking' | string;
  severidad: Severidad;
  mensaje: string;
}

export interface Resumen {
  org_id: string;
  desde: string;
  hasta: string;
  moneda: string;
  presupuesto: {
    mes: string;
    tope_cents: number;
    gastado_cents: number;
    ritmo: Ritmo;
    por_canal: PresupuestoCanal[];
  } | null;
  totales: Totales;
  por_canal: FilaCanal[];
  por_campana: FilaCampana[];
  serie_diaria: PuntoDiario[];
  embudo: { visitas: number; leads: number; checkouts: number; compras: number };
  alertas: Alerta[];
  actualizado: string;
}

export interface Presupuesto {
  mes: string;
  moneda: string;
  tope_cents: number;
  por_canal: { canal: Canal; tope_cents: number }[];
  alertas_pct: number[];
}

export interface CuentaMedicion {
  canal: Canal;
  id: string;
  nombre: string;
  paga: string;
  estado: 'ok' | 'sin_pago' | 'error' | string;
}

export interface Medicion {
  meta: {
    pixel_id: string | null;
    capi: boolean;
    ultimo_evento: string | null;
    eventos_24h: Record<string, number>;
  };
  google: { ga4_id: string | null; ads_id: string | null; conectado: boolean };
  cuentas: CuentaMedicion[];
  problemas: string[];
}

// ── Errores ───────────────────────────────────────────────────

/** El backend todavía no tiene el endpoint (404/405/501): estado «pendiente». */
export class MarketingNoDisponible extends Error {
  constructor() {
    super('El servidor todavía no tiene el tablero de Marketing.');
    this.name = 'MarketingNoDisponible';
  }
}

// ── Demostración ──────────────────────────────────────────────

const MOCK_KEY = 'marketing_os_demo';

export function demoActiva(): boolean {
  if (import.meta.env.VITE_MARKETING_OS_MOCK === '1') return true;
  try {
    return sessionStorage.getItem(MOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDemo(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(MOCK_KEY, '1');
    else sessionStorage.removeItem(MOCK_KEY);
  } catch {
    /* sin almacenamiento: la demo no se recuerda, no pasa nada */
  }
}

// ── Transporte ────────────────────────────────────────────────

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function djangoFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  if (res.status === 404 || res.status === 405 || res.status === 501) throw new MarketingNoDisponible();
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as { detail?: unknown }).detail || `Error ${res.status} del servidor`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : '';
}

// ── Endpoints ─────────────────────────────────────────────────

export async function fetchResumen(desde: string, hasta: string): Promise<Resumen> {
  if (demoActiva()) return mockResumen(desde, hasta);
  return djangoFetch<Resumen>(`${BASE}/resumen${qs({ desde, hasta })}`);
}

export async function fetchPresupuesto(mes: string): Promise<Presupuesto> {
  if (demoActiva()) return mockPresupuesto(mes);
  return djangoFetch<Presupuesto>(`${BASE}/presupuesto${qs({ mes })}`);
}

export async function savePresupuesto(p: Presupuesto): Promise<Presupuesto> {
  if (demoActiva()) return { ...p };
  return djangoFetch<Presupuesto>(`${BASE}/presupuesto${qs({ mes: p.mes })}`, {
    method: 'PUT',
    body: JSON.stringify(p),
  });
}

export async function fetchMedicion(): Promise<Medicion> {
  if (demoActiva()) return mockMedicion();
  return djangoFetch<Medicion>(`${BASE}/medicion`);
}
